import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useGame } from '../context/GameContext';

const { FiTrendingUp, FiUsers, FiClock, FiStar, FiZap, FiCoffee } = FiIcons;

function IdleUpgrades() {
  const { state, dispatch } = useGame();

  const idleUpgrades = [
    {
      id: 'auto_espresso',
      name: 'Auto Espresso Machine',
      description: 'Automatically pulls perfect shots',
      baseCost: 500,
      currentLevel: state.cafeUpgrades.autoEspresso || 0,
      maxLevel: 5,
      effect: 'Espresso efficiency',
      icon: FiCoffee,
      color: 'from-amber-500 to-amber-600'
    },
    {
      id: 'smart_steamer',
      name: 'Smart Milk Steamer',
      description: 'AI-controlled milk texturing',
      baseCost: 750,
      currentLevel: state.cafeUpgrades.smartSteamer || 0,
      maxLevel: 5,
      effect: 'Milk drink speed',
      icon: FiZap,
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'customer_magnet',
      name: 'Customer Magnet',
      description: 'Attracts more idle customers',
      baseCost: 1000,
      currentLevel: state.cafeUpgrades.customerMagnet || 0,
      maxLevel: 3,
      effect: 'Customer frequency',
      icon: FiUsers,
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 'speed_boost',
      name: 'Speed Boost System',
      description: 'Reduces all preparation times',
      baseCost: 1200,
      currentLevel: state.cafeUpgrades.speedBoost || 0,
      maxLevel: 3,
      effect: 'Overall speed',
      icon: FiClock,
      color: 'from-green-500 to-green-600'
    },
    {
      id: 'quality_enhancer',
      name: 'Quality Enhancer',
      description: 'Improves drink quality automatically',
      baseCost: 1500,
      currentLevel: state.cafeUpgrades.qualityEnhancer || 0,
      maxLevel: 3,
      effect: 'Drink quality',
      icon: FiStar,
      color: 'from-yellow-500 to-yellow-600'
    }
  ];

  const calculateCost = (baseCost, level) => {
    return Math.floor(baseCost * Math.pow(1.5, level));
  };

  const canAfford = (cost) => state.coins >= cost;

  const purchaseUpgrade = (upgrade) => {
    const cost = calculateCost(upgrade.baseCost, upgrade.currentLevel);
    if (!canAfford(cost) || upgrade.currentLevel >= upgrade.maxLevel) return;

    dispatch({ type: 'SPEND_COINS', payload: cost });
    dispatch({
      type: 'UPGRADE_CAFE',
      payload: {
        item: upgrade.id,
        level: upgrade.currentLevel + 1
      }
    });
  };

  const getEffectDescription = (upgrade) => {
    const level = upgrade.currentLevel;
    const effectValue = (level + 1) * 20;

    switch (upgrade.id) {
      case 'auto_espresso':
        return `+${effectValue}% espresso speed`;
      case 'smart_steamer':
        return `+${effectValue}% milk prep speed`;
      case 'customer_magnet':
        return `+${level + 1} customers/min`;
      case 'speed_boost':
        return `+${effectValue}% all speeds`;
      case 'quality_enhancer':
        return `+${effectValue}% quality bonus`;
      default:
        return `Level ${level + 1} effect`;
    }
  };

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
      <div className="flex items-center mb-4">
        <SafeIcon icon={FiTrendingUp} className="text-purple-500 mr-2 text-lg" />
        <div>
          <h3 className="font-bold text-gray-800">Idle Upgrades</h3>
          <p className="text-xs text-gray-500">Boost your automated cafe</p>
        </div>
      </div>

      <div className="space-y-3">
        {idleUpgrades.map((upgrade) => {
          const cost = calculateCost(upgrade.baseCost, upgrade.currentLevel);
          const isMaxLevel = upgrade.currentLevel >= upgrade.maxLevel;
          const canBuy = canAfford(cost) && !isMaxLevel;

          return (
            <motion.div
              key={upgrade.id}
              className={`border-2 rounded-xl p-3 transition-all ${
                isMaxLevel
                  ? 'border-green-300 bg-green-50'
                  : canBuy
                    ? 'border-blue-300 bg-blue-50'
                    : 'border-gray-200 bg-gray-50'
              }`}
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${upgrade.color} flex items-center justify-center mr-3`}>
                    <SafeIcon icon={upgrade.icon} className="text-white text-sm" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800 text-sm">
                      {upgrade.name}
                    </h4>
                    <p className="text-xs text-gray-600">
                      {upgrade.description}
                    </p>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-xs text-gray-500">
                    Level {upgrade.currentLevel}/{upgrade.maxLevel}
                  </div>
                  {!isMaxLevel && (
                    <div className="text-sm font-bold text-green-600">
                      ${cost}
                    </div>
                  )}
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-2">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <motion.div
                    className={`h-2 rounded-full bg-gradient-to-r ${upgrade.color}`}
                    style={{ width: `${(upgrade.currentLevel / upgrade.maxLevel) * 100}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>

              {/* Effect and Button */}
              <div className="flex items-center justify-between">
                <div className="text-xs text-purple-600 font-medium">
                  {getEffectDescription(upgrade)}
                </div>

                {!isMaxLevel ? (
                  <motion.button
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                      canBuy
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    whileHover={canBuy ? { scale: 1.05 } : {}}
                    whileTap={canBuy ? { scale: 0.95 } : {}}
                    onClick={() => canBuy && purchaseUpgrade(upgrade)}
                    disabled={!canBuy}
                  >
                    {canBuy ? 'Upgrade' : 'Not enough coins'}
                  </motion.button>
                ) : (
                  <div className="px-3 py-1 bg-green-200 text-green-700 rounded-lg text-xs font-bold">
                    MAX
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Total Benefits */}
      <div className="mt-4 p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl">
        <h4 className="font-bold text-gray-800 text-sm mb-2">Active Benefits:</h4>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center">
            <SafeIcon icon={FiZap} className="text-purple-500 mr-1" />
            <span>+{((state.cafeUpgrades.autoEspresso || 0) + (state.cafeUpgrades.speedBoost || 0)) * 20}% Speed</span>
          </div>
          <div className="flex items-center">
            <SafeIcon icon={FiUsers} className="text-blue-500 mr-1" />
            <span>+{(state.cafeUpgrades.customerMagnet || 0)} Customers/min</span>
          </div>
          <div className="flex items-center">
            <SafeIcon icon={FiStar} className="text-yellow-500 mr-1" />
            <span>+{(state.cafeUpgrades.qualityEnhancer || 0) * 20}% Quality</span>
          </div>
          <div className="flex items-center">
            <SafeIcon icon={FiTrendingUp} className="text-green-500 mr-1" />
            <span>Total: Level {Object.values(state.cafeUpgrades).reduce((a, b) => a + (b || 0), 0)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IdleUpgrades;